<template>
    <BaseDialog v-model:open="isOpen" :title="t('connections.facetFilter.title')" show-close mobile-height="expanded">
        <template #trigger>
            <!-- FIXME: Replace the Filter trigger with a toggle button instead of switching to the primary theme. -->
            <BaseButton :theme="activeCount > 0 ? 'primary' : 'neutral'" size="sm" class="gap-2 rounded-full">
                <i class="bi bi-funnel" aria-hidden="true"></i>
                {{ t("connections.facetFilter.trigger") }}
            </BaseButton>
        </template>
        <div class="shrink-0 pb-4">
            <p class="body-small text-ink-muted">{{ t("connections.facetFilter.description") }}</p>
            <div v-if="activeCount > 0" class="body-small-semibold mt-3 inline-flex items-center gap-2 rounded-full bg-chip-neutral-bg px-3 py-1 text-chip-neutral-text">
                <i class="bi bi-sliders2 text-sm" aria-hidden="true"></i>
                {{ t("connections.facetFilter.activeCount", { count: activeCount }, activeCount) }}
            </div>
        </div>
        <div class="space-y-4">
            <div v-for="facet in facets" :key="facet.id" class="rounded-xl border border-card-border p-4 space-y-2">
                <div class="flex items-center justify-between gap-3">
                    <div class="body-small-semibold text-ink">{{ facet.name }}</div>
                    <span v-if="localFilters[facet.id]" class="body-small text-ink-muted">{{ localFilters[facet.id] }}</span>
                </div>
                <BaseSelect
                    :aria-label="t('connections.facetFilter.optionsLabel', { facet: facet.name })"
                    :model-value="localFilters[facet.id] || ALL_FILTER_OPTION"
                    :options="facetOptions(facet)"
                    trigger-class="w-full!"
                    @update:model-value="(val: string) => updateFilter(facet.id, val)" />
            </div>
        </div>
        <template #footer>
            <BaseButton v-if="hasPendingSelections" theme="tertiary" size="sm" @click="clearAll"> {{ t("connections.facetFilter.clearAll") }} </BaseButton>
            <span v-else class="body-small text-ink-muted">{{ t("connections.facetFilter.noneSelected") }}</span>
            <BaseButton theme="primary" size="sm" @click="applyFilters">{{ t("connections.facetFilter.apply") }}</BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

interface Facet {
    id: string;
    name: string;
    values: string[];
}

interface Props {
    facets: Facet[];
}

const ALL_FILTER_OPTION = "__all__";

defineProps<Props>();
const { t } = useI18n();
const model = defineModel<Record<string, string>>({ default: () => ({}) });

const isOpen = ref(false);
const localFilters = ref<Record<string, string>>({ ...model.value });

watch(model, (val) => {
    localFilters.value = { ...val };
});

watch(isOpen, (open) => {
    if (open) {
        localFilters.value = { ...model.value };
    }
});

const activeCount = computed(() => Object.values(model.value).filter((v) => v).length);
const hasPendingSelections = computed(() => Object.values(localFilters.value).some((v) => v));

const facetOptions = (facet: Facet) => [{ key: ALL_FILTER_OPTION, label: t("connections.facetFilter.all") }, ...facet.values.map((v) => ({ key: v, label: v }))];

const updateFilter = (facetId: string, value: string) => {
    if (value && value !== ALL_FILTER_OPTION) {
        localFilters.value[facetId] = value;
        return;
    }

    delete localFilters.value[facetId];
    localFilters.value = { ...localFilters.value };
};

const clearAll = () => {
    localFilters.value = {};
};

const applyFilters = () => {
    model.value = { ...localFilters.value };
    isOpen.value = false;
};
</script>
